// The Eng Tour "Export everything" download — the pure half.
//
// The Tour Creator's Export button gathers the live tour document, every
// retained Edit History snapshot and every photo they reference, and hands the
// lot over as one zip. Fetching lives in EngTourCreator (it needs the session
// token and a progress line); deciding WHAT to fetch and how the zip is laid
// out lives here, where `src/engtour.export.test.js` can pin it.
//
// Why this exists at all: the live tour is a single KV document
// (`engtour:tours`), its history is capped at 50 saves and each snapshot
// expires after 180 days, and the photos are Blob URLs nothing else lists.
// None of that is a backup. This download is the operator's own copy, taken
// from the live store rather than from whatever is on screen, and it can be
// restored without any tool but the creator's own save endpoint.

const IMAGE_EXT = /\.(jpe?g|png|webp|gif|avif|heic)(\?|#|$)/i;

/** Is this string a photo the tour content points at (as opposed to a web link)? */
export function isImageRef(s) {
  const t = String(s || "").trim();
  if (!t) return false;
  if (/^https?:\/\//i.test(t)) {
    let host = "";
    try { host = new URL(t).hostname; } catch { return false; }
    // Everything uploaded through the creator lands in Vercel Blob, whatever
    // the file was called; anything else only counts if it looks like an image.
    if (/(^|\.)vercel-storage\.com$/i.test(host)) return true;
    return IMAGE_EXT.test(t);
  }
  // A relative library path ("Map Info/EngTour/tourimages/x.jpg", with or
  // without the leading slash) — the dev-server upload target and the seed data.
  return /^\/?Map Info\//.test(t) && IMAGE_EXT.test(t);
}

// Inline media tokens in a stop body: [[img|url]], [[video|url|autoplay]],
// [[link|url|label]]. Only img is a file we hold; the others are links out.
const TOKEN_RE = /\[\[\s*([a-z]+)\s*\|\s*([^\]|]+?)\s*(?:\|[^\]]*)?\]\]/gi;

/**
 * Every image reference anywhere in a tour document (or a list of them),
 * deduplicated, in first-seen order. Walks every string rather than naming
 * fields, so a photo on a field added next month is still in the export.
 */
export function collectImageRefs(...docs) {
  const out = [];
  const seen = new Set();
  const add = (s) => {
    const t = String(s).trim();
    if (!seen.has(t) && isImageRef(t)) { seen.add(t); out.push(t); }
  };
  const walk = (v, depth) => {
    if (depth > 40 || v == null) return;
    if (typeof v === "string") {
      add(v);
      let m;
      TOKEN_RE.lastIndex = 0;
      while ((m = TOKEN_RE.exec(v))) if (m[1].toLowerCase() === "img") add(m[2]);
      return;
    }
    if (Array.isArray(v)) { v.forEach((x) => walk(x, depth + 1)); return; }
    if (typeof v === "object") Object.values(v).forEach((x) => walk(x, depth + 1));
  };
  docs.forEach((d) => walk(d, 0));
  return out;
}

/** The URL to fetch an image reference from, given the site's origin. */
export function resolveImageUrl(ref, origin) {
  if (/^https?:\/\//i.test(ref)) return ref;
  return `${String(origin || "").replace(/\/$/, "")}/${encodeURI(ref.replace(/^\//, ""))}`;
}

/** A filename for a fetched image inside images/: the URL's own name, made safe and unique. */
export function imageFileName(ref, taken) {
  let base = ref;
  try { base = decodeURIComponent(new URL(ref, "https://x.invalid").pathname.split("/").pop() || ""); } catch { /* keep ref */ }
  base = base.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "image";
  if (!/\.[A-Za-z0-9]{2,5}$/.test(base)) base += ".bin";
  let name = base, n = 2;
  while (taken.has(name)) { name = base.replace(/(\.[^.]+)$/, `-${n}$1`); n++; }
  taken.add(name);
  return name;
}

const fmtDate = (ts) => new Date(ts).toISOString().replace("T", " ").slice(0, 19) + " UTC";

/**
 * Lay the export out as zip entries. `snapshots` is `{ [ts]: doc }` for the
 * versions that could still be fetched; `images` is `[{ ref, name, bytes }]`
 * and `failedImages` is `[{ ref, reason }]`.
 */
export function buildExportFiles({ live, versions = [], snapshots = {}, images = [], failedImages = [], takenAt = Date.now(), origin = "" }) {
  const stamp = new Date(takenAt).toISOString().slice(0, 10);
  const root = `engtour-export-${stamp}`;
  const files = [];
  const json = (o) => JSON.stringify(o, null, 2) + "\n";

  files.push({ path: `${root}/live/tours.json`, data: json(live) });

  const index = versions.map((v) => ({
    ts: v.ts,
    savedAt: fmtDate(v.ts),
    user: v.user || null,
    changes: Array.isArray(v.changes) ? v.changes : [],
    file: snapshots[v.ts] ? `history/${v.ts}.json` : null,
  }));
  files.push({ path: `${root}/history/index.json`, data: json(index) });
  for (const v of versions) {
    if (snapshots[v.ts]) files.push({ path: `${root}/history/${v.ts}.json`, data: json(snapshots[v.ts]) });
  }

  for (const im of images) files.push({ path: `${root}/images/${im.name}`, data: im.bytes });
  files.push({
    path: `${root}/images/manifest.json`,
    data: json({
      saved: images.map((im) => ({ ref: im.ref, file: `images/${im.name}`, bytes: im.bytes.length })),
      notSaved: failedImages,
    }),
  });

  const tours = Array.isArray(live?.tours) ? live.tours : [];
  const stops = tours.reduce((n, t) => n + (Array.isArray(t.stops) ? t.stops.length : 0), 0);
  const expired = versions.length - Object.keys(snapshots).length;
  files.unshift({ path: `${root}/README.txt`, data: [
    `UMaps Engineering Tour — full export`,
    `Taken ${fmtDate(takenAt)} from ${origin || "the live site"}.`,
    ``,
    `This is everything the Tour Creator holds, copied straight from the live store`,
    `(not from the editor screen, so unsaved edits are not in it).`,
    ``,
    `live/tours.json       The current tour content: welcome screen, resources,`,
    `                      questionnaire, ${tours.length} tour${tours.length === 1 ? "" : "s"} with ${stops} stop${stops === 1 ? "" : "s"}, the`,
    `                      free-roam places, custom spots and the stop bank.`,
    `                      !! The stop bank includes the scavenger-hunt door-quote`,
    `                      answers, which the public tour never receives. Treat this`,
    `                      file as private.`,
    `history/index.json    Edit History — every retained save, newest first, with`,
    `                      who saved it and what changed.`,
    `history/<ts>.json     The full tour content as it was at that save`,
    `                      (${Object.keys(snapshots).length} of ${versions.length} saves${expired > 0 ? `; ${expired} had already expired from the store` : ""}).`,
    `images/               Every photo the content points at, where it could be`,
    `                      fetched; images/manifest.json maps each URL to its file and`,
    `                      lists any that could not be saved.`,
    ``,
    `To restore the live tour from this export: send live/tours.json to the`,
    `creator's save endpoint as { "op": "save", "data": <the file> } with an editor`,
    `session (POST /api/tour-admin), or copy it over data/engtour/tours.json to seed`,
    `a local dev server. A history snapshot restores the same way.`,
    ``,
  ].join("\n") });

  return files;
}
